import { type FC, type PropsWithChildren, useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import { isIOS } from 'react-device-detect'
import { PageWithRefresh, SubPageWithRefresh } from '@dhl-ui/page'
import { Box, useIsMobileVariant } from '@dhl-ui/components'
import { DocumentTitle } from '@dhl/receiver-a11y'
import { FixedBottomBoxHeightProvider, FixedBottomBoxSpacer } from '@dhl/receiver-design'
import { type SubPageProps } from './types.js'

const Loading = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
    }}
  >
    <CircularProgress size={40} />
  </Box>
)

/**
 * On iOS we need to use a fixed container to prevent the page from jumping when the keyboard opens and
 * the scroll position is adjusted. By using a fixed container, we can ensure that the content remains
 * in place and the keyboard does not cause any unexpected layout shifts.
 */
const Container = ({ children }: PropsWithChildren) => {
  if (isIOS) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '100dvh',
          overflowY: 'auto',
          // The SubPage renders its own `position: fixed` toolbar at the top
          // of this scroll container. Without `scroll-padding-top` any call
          // to `Element.scrollIntoView()` (which is what the browser — and
          // Cypress — use when clicking an off-screen element) scrolls the
          // target to y=0, placing it underneath the fixed toolbar.
          scrollPaddingTop: (theme) => theme.mixins.toolbar.minHeight,
        }}
      >
        {children}
      </Box>
    )
  }

  return children
}

export const SubPage: FC<SubPageProps> = ({
  isFullView,
  children,
  endNodes,
  hideChildrenWhileLoading = false,
  isLoading,
  title,
  'data-testid': dataTestId,
  ...rest
}) => {
  const isMobile = useIsMobileVariant()

  useEffect(() => {
    document.getElementById('root')?.scrollTo(0, 0)
  }, [])

  return (
    <Container>
      <DocumentTitle title={title} />

      <SubPageWithRefresh
        data-testid={dataTestId}
        endNodes={
          <>
            {endNodes}
            {
              // TODO: ask UX if this is wanted or really skeleton per page...
              isLoading && !hideChildrenWhileLoading && (
                <Box px={1}>
                  <CircularProgress size={24} />
                </Box>
              )
            }
          </>
        }
        title={title}
        {...rest}
      >
        <PageWithRefresh.Content isFullView={isFullView} isMaximizedCentered={!isMobile}>
          <FixedBottomBoxHeightProvider>
            {isLoading && hideChildrenWhileLoading && <Loading />}
            {isLoading && hideChildrenWhileLoading ? null : children}
            <FixedBottomBoxSpacer />
          </FixedBottomBoxHeightProvider>
        </PageWithRefresh.Content>
      </SubPageWithRefresh>
    </Container>
  )
}
